import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sessions } from '../lib/api';
import Layout from '../components/Layout';

export default function SessionDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['session', id],
    queryFn: () => sessions.get(id!),
    enabled: !!id,
  });

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      why: '目的・背景',
      who: 'ユーザー',
      what: '機能・データ',
      flow: '業務フロー',
      vibe: 'デザイン・雰囲気',
    };
    return labels[category] || category;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    const labels: Record<string, string> = {
      in_progress: '進行中',
      completed: '完了',
      paused: '一時停止',
      cancelled: 'キャンセル',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.cancelled}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (error || !session) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-500">セッションが見つかりません</p>
          <Link to="/sessions" className="btn-outline mt-4 inline-block">
            一覧に戻る
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* ヘッダー */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/sessions" className="text-jarvis-gold hover:underline">
                ← 戻る
              </Link>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              セッション詳細
            </h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              {session.initialIdea?.substring(0, 100) || '（未入力）'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(session.status)}
            {session.status === 'completed' && (
              <Link to={`/sessions/${id}/blueprint`} className="btn-primary">
                設計書を表示
              </Link>
            )}
            {session.status === 'paused' && (
              <Link to="/" className="btn-primary">
                再開する
              </Link>
            )}
          </div>
        </div>

        {/* 進捗 */}
        <div className="card">
          <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            進捗状況
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-jarvis-gold transition-all"
                  style={{ width: `${(session.answeredQuestions / session.totalQuestions) * 100}%` }}
                />
              </div>
            </div>
            <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              {session.answeredQuestions} / {session.totalQuestions}
            </span>
          </div>
        </div>

        {/* 質問一覧 */}
        <div className="card">
          <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            質問と回答
          </h2>
          <div className="space-y-4">
            {session.questions?.map((q) => (
              <div
                key={q.id}
                className={`p-4 rounded-lg border ${
                  q.isAnswered
                    ? 'border-green-500/30 bg-green-500/5'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs rounded bg-jarvis-gold/20 text-jarvis-gold">
                    {getCategoryLabel(q.category)}
                  </span>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Q{q.questionNumber}
                  </span>
                  {q.isAnswered && (
                    <span className="text-green-500 text-sm">✓ 回答済み</span>
                  )}
                </div>
                <p style={{ color: 'var(--text-primary)' }}>{q.questionText}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 会話ログ */}
        {session.conversationLogs && session.conversationLogs.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              会話ログ
            </h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {session.conversationLogs.map((log) => (
                <div
                  key={log.id}
                  className={`p-3 rounded-lg ${
                    log.role === 'jarvis'
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : 'bg-jarvis-gold/10 ml-8'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                      {log.role === 'jarvis' ? 'Jarvis' : 'あなた'}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(log.createdAt).toLocaleString('ja-JP')}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text-secondary)' }}>
                    {log.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

